import type { BinaryImage } from "./types";

/**
 * Binary morphology with a square structuring element, implemented as two
 * separable passes (horizontal then vertical) for efficiency. Out-of-bounds
 * neighbours are ignored, which preserves shapes that touch the image border.
 */

type Op = "dilate" | "erode";

function pass(
  src: Uint8Array,
  width: number,
  height: number,
  radius: number,
  op: Op,
  horizontal: boolean,
): Uint8Array {
  const out = new Uint8Array(src.length);
  const dilate = op === "dilate";
  const length = horizontal ? width : height;

  for (let line = 0; line < (horizontal ? height : width); line++) {
    for (let i = 0; i < length; i++) {
      let value = dilate ? 0 : 1;
      for (let d = -radius; d <= radius; d++) {
        const j = i + d;
        if (j < 0 || j >= length) continue;
        const idx = horizontal ? line * width + j : j * width + line;
        if (dilate) {
          if (src[idx] === 1) {
            value = 1;
            break;
          }
        } else if (src[idx] === 0) {
          value = 0;
          break;
        }
      }
      const outIdx = horizontal ? line * width + i : i * width + line;
      out[outIdx] = value;
    }
  }
  return out;
}

function morph(image: BinaryImage, radius: number, op: Op): BinaryImage {
  if (radius <= 0) {
    return { ...image, data: new Uint8Array(image.data) };
  }
  const h = pass(image.data, image.width, image.height, radius, op, true);
  const data = pass(h, image.width, image.height, radius, op, false);
  return { width: image.width, height: image.height, data };
}

export function dilate(image: BinaryImage, radius: number): BinaryImage {
  return morph(image, radius, "dilate");
}

export function erode(image: BinaryImage, radius: number): BinaryImage {
  return morph(image, radius, "erode");
}

/** Morphological closing (dilate then erode): bridges thin gaps and fills small holes. */
export function close(image: BinaryImage, radius: number): BinaryImage {
  return erode(dilate(image, radius), radius);
}

/** Morphological opening (erode then dilate): removes small speckles. */
export function open(image: BinaryImage, radius: number): BinaryImage {
  return dilate(erode(image, radius), radius);
}
